import {useState, useMemo, useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import BMPTableHead from './bmp-table-head';
import style from '../../common-style/common-style.module.css';
import { dateFormatter } from '../../../../helpers/brand-manager-portal-utils';
import LoadingButton from '@mui/lab/LoadingButton';
import EditIcon from '@mui/icons-material/Edit';
import BMPReviewThemeTable from './theme-table';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import Tooltip from '@mui/material/Tooltip';
import BmpStatus from '../Status/Status';
// import { stableSort, getComparator, statusColor} from '../../utils/functions';
import { getComparator, stableSort, statusColor} from '../../utils/functions';
import useHttp from '../../hooks/useHttp';
import BmpBsci from '../BSCI/bsci';
import ScoreInsights from '../score-insight/scores-insights';
import { MESSAGES } from '../../utils/status';
import { OutlinedInput } from '@mui/material';


export default function BMPReviewTable({loadedData, selected, setSelected, headCells, setRefreshData, pendingThemeIds, setPendingThemeIds, locale, setSnackbarState, page, setPage, rowsPerPage, setRowsPerPage}) {
    const loadedDataList = useMemo(() =>  loadedData || [], [loadedData]);
    const rows = loadedDataList?.data;
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('content_status');
    const inputRef = useRef(null);
    const [editSummary, setEditSummary] = useState({});
    const { sendRequest, error } = useHttp();
    const [selectedByPage, setSelectedByPage] = useState([]);
    const charLimit = 600;

    useEffect(()=>{
        let isMounted = true;
        if (isMounted && Object.keys(editSummary).length && inputRef.current && document.activeElement !== inputRef.current) {
            const getId = Object.keys(editSummary).filter(item => item.slice(0,3) === 'id_');
            const selId = inputRef.current.dataset.focusable;
            if (selId && getId.includes(selId)) {
                const textarea = inputRef.current;
                textarea.focus();
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
        }
        return () => {
            isMounted = false; // Cleanup function to set flag to false
        };
    }, [editSummary]);

    useEffect(()=>{
        const rowIds = rows.map(item => item.doc_id);
        const selectedIds = selected.map(item => item.doc_id);
        const sbp = rowIds.filter(id => selectedIds.includes(id));
        setSelectedByPage(sbp);
    }, [rows, selected])

    const handleEditSummary = (id, text) => {
        setEditSummary((prev) => ({
            ...prev,
            [`id_${id}`]: true,
            [`load_${id}`]: false,
            [`text_${id}`]: text,
            [`error_${id}`]: null
        }));
    };

    const handleSaveSummary = async (id, params) => { 
        // const activeList = rows.find(item => item.doc_id === id);
        const {subCollection_uid, summary_id, summary: prevText} = params;
        const text = editSummary[`text_${id}`] || '';

        if (text === '') {
            const ErrorMessage = 'Summary cannot be empty';
            setEditSummary((prev) => ({
                ...prev,
                [`error_${id}`]: ErrorMessage
            }));
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: ErrorMessage,
                severity: 'error'
            }));
            return;
        }

        if (text.length > charLimit) {
            const ErrorMessage = `Summary should not be more then ${charLimit} charactors.`;
            setEditSummary((prev) => ({
                ...prev,
                [`error_${id}`]: ErrorMessage,
            }));
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: ErrorMessage,
                severity: 'error'
            }));
            return;
        }

        if (text === prevText) {
            setEditSummary((prev) => ({
                ...prev,
                [`id_${id}`]: false
            }));
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: MESSAGES.noChange,
                severity: 'info'
            }));
            return;
        }

        setEditSummary((prev) => ({
            ...prev,
            [`load_${id}`]: true,
            [`error_${id}`]: null
        }));
        
        try {
            const data = [{
                subCollection_uid,
                "doc_id": id,
                "action": "save",
                summary_id,
                "locale": locale,
                "summary": text
            }];
            const response = await sendRequest('summary/update', 'POST', data);
            if (response && response[0].status === 500) {
                throw new Error(response[0].message);
            }
            setRefreshData(true);
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: MESSAGES.saved,
                severity: 'success'
            }));
            setEditSummary((prev) => ({
                ...prev,
                [`text_${id}`]: text
            }));
        } catch (err) {
            setSnackbarState(prev => ({
                ...prev,
                open: true,
                message: err.message || MESSAGES.dataFailed,
                severity: 'error'
            }));
            console.error(error);
        }
        setEditSummary((prev) => ({
            ...prev,
            [`id_${id}`]: false,
            [`load_${id}`]: false,
        }));
    };

    const handleCancelSummary = (id) => { 
        setEditSummary((prev) => ({
            ...prev,
            [`id_${id}`]: false,
            [`text_${id}`]: null,
            [`error_${id}`]: null
        }));
    };

    const handleSummaryChange = (id, newValue) => {
        if (newValue === '' || newValue.length > charLimit) {
            setEditSummary((prev) => ({
                ...prev,
                [`error_${id}`]: newValue === '' ? 'Summary cannot be empty' : `Summary should not ne more then ${charLimit} charactors.`,
                [`text_${id}`]: newValue
            }));
        } else {
            setEditSummary((prev) => ({
                ...prev,
                [`text_${id}`]: newValue,
                [`error_${id}`]: null
            }));
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            // const newSelected = rows.map((n) => n.doc_id);
            // selectedHandler(newSelected);
            setSelected(prev => [ ...prev, ...rows ]);
            return; 
        }
        const rowId = rows.map(item => item.doc_id);
        const newSelected = selected.filter(item => !rowId.includes(item.doc_id));
        setSelected(newSelected);
    };

    const handleClick = (id) => {
        // let prevSelected = selected;
        // const selectedIndex = prevSelected.indexOf(id);
        // let newSelected = [];

        // if (selectedIndex === -1) {
        //     newSelected = newSelected.concat(prevSelected, id);
        // } else if (selectedIndex === 0) {
        //     newSelected = newSelected.concat(prevSelected.slice(1));
        // } else if (selectedIndex === prevSelected.length - 1) {
        //     newSelected = newSelected.concat(prevSelected.slice(0, -1));
        // } else if (selectedIndex > 0) {
        //     newSelected = newSelected.concat(
        //         prevSelected.slice(0, selectedIndex),
        //         prevSelected.slice(selectedIndex + 1),
        //     );
        // }
        // setSelected(newSelected);
        setSelected(prevSelected => {
            const selectedIndex = prevSelected.findIndex(item => item.doc_id === id);
            let newSelected = [];

            if (selectedIndex === -1) {
                const selectedRow = rows.find(item => item.doc_id === id);
                // Item not found in selected array, add it
                newSelected = [...prevSelected, {...selectedRow}];  
            } else {
                // Item found, remove it.  Use filter.
                newSelected = prevSelected.filter(item => item.doc_id !== id);
            }
            return newSelected;
        });
    };

    // PAGINATION
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        const value = parseInt(event.target.value, 10);
        setRowsPerPage(value);
        setPage(0);
    };
    // PAGINATION

    const selectedIds = selected.map(item => item.doc_id);
    const isSelected = (id) => selectedIds.indexOf(id) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    // PAGINATION
    // const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
    // const visibleRows = useMemo(() => stableSort(rows, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [rows, order, orderBy, page, rowsPerPage, refreshData]);
    // const visibleRows = rows;
    const visibleRows = useMemo(() => {
        // return stableSort(rows, getComparator(order, orderBy)); // Create a copy to avoid mutating the original data
        if (order === 'asc') {
            return stableSort(rows, getComparator(order, orderBy, 'skuSummary')); // Create a copy to avoid mutating the original data
        }
        return rows;
    }, [rows, order, orderBy]);

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2, overflowX: 'auto' }}>
            <TableContainer>
                <Table
                    className={style['custom-table']}
                    sx={{ minWidth: 750 }}
                    aria-labelledby="tableTitle"
                    size='medium'
                >
                <BMPTableHead
                    headCells={headCells}
                    numSelected={selectedByPage.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={visibleRows.length}
                />
                <TableBody>
                    {visibleRows.map((row, index) => {
                        const isItemSelected = isSelected(row.doc_id);
                        const labelId = `enhanced-table-checkbox-${index}`;
                        const status = row.skuSummary?.content_status;
                        const themes = row?.skuSummary?.themes || [];
                        const bsciScore =  row.skuSummary?.BSCI;

                        return (
                            <TableRow
                                key={row.doc_id}
                                sx={{ 
                                    backgroundColor: statusColor(status),
                                    "&:hover": {
                                        backgroundColor: statusColor(status, true)
                                    },
                                 }}
                            >
                                <TableCell sx={{ padding: 0 }}>
                                    <Checkbox
                                        color="primary"
                                        onChange={() => handleClick(row.doc_id)}
                                        checked={isItemSelected}
                                        inputProps={{
                                            'aria-labelledby': labelId,
                                        }}
                                />
                                </TableCell>
                                <TableCell id={labelId} className={style['table-left-align']}>
                                    {<>
                                        {row.pageUrl ? (
                                            <a href={row.pageUrl} className={style['table-titlw-warp']} target='_blank' rel='noreferrer'><Tooltip title={row.title}><span>{row.title}</span></Tooltip></a>
                                        ) : (
                                            <div className={style['table-titlw-warp']}><Tooltip title={row.title}><span>{row.title}</span></Tooltip></div>
                                        )}
                                        <strong>{row.sku_id}</strong>
                                    </>}
                                </TableCell>
                                <TableCell>
                                    REC: {row.reviewSummary?.reviewCount} <br/>
                                    AVR: {row.reviewSummary?.averageRating}<br/>
                                    RAC: {row.reviewSummary?.ratingCount}
                                </TableCell>
                                
                                <TableCell className={style['table-left-align']}>
                                    {editSummary[`id_${row.doc_id}`] ? (
                                        <Box sx={{display:'flex', gap:'15px'}}>
                                            <div className={`${style.editableSummary} ${editSummary[`error_${row.doc_id}`] ? style.textareaError : ''}`}>
                                                {/* <textarea 
                                                    className={`${style.editableSummary} `}
                                                    value={editSummary[`text_${row.doc_id}`]}
                                                    onChange={(e) => handleSummaryChange(row.doc_id, e.target.value)}
                                                    disabled={editSummary[`load_${row.doc_id}`] || false}
                                                ></textarea> */}
                                                <OutlinedInput
                                                    id={`summary-data-${row.doc_id}}`}
                                                    label=""
                                                    multiline
                                                    variant='filled'
                                                    inputRef={inputRef}
                                                    disabled={editSummary[`load_${row.doc_id}`] || false}
                                                    value={editSummary[`text_${row.doc_id}`]}
                                                    onChange={(e) => handleSummaryChange(row.doc_id, e.target.value)}
                                                    inputProps={{
                                                        'aria-label': 'Theme',
                                                        'data-focusable': editSummary[`id_${row.doc_id}`] ? `id_${row.doc_id}` : ''
                                                    }}
                                                    sx={{
                                                        width:'100%',
                                                        height:'100%',
                                                        backgroundColor:'#fff',
                                                        borderRadius: '4px',
                                                        padding: '12px 10px',
                                                        // maxHeight: '200px',
                                                        "textarea": {
                                                            width: '100%',
                                                            minHeight: '100px',
                                                            maxHeight: '250px',
                                                            resize: 'auto'
                                                        },
                                                        "fieldset": {
                                                            borderColor: 'rgba(0, 0, 0, 0.9)'
                                                        }
                                                    }}
                                                />
                                                {/* {editSummary[`error_${row.doc_id}`] && <Box sx={{color:'#f72424', fontSize: '.8rem'}}>{editSummary[`error_${row.doc_id}`]}</Box>} */}
                                            </div>
                                            <Box sx={{textAlign: 'right', display:'flex', flexDirection: 'column', gap: '10px'}}>
                                                <LoadingButton
                                                    onClick={() => handleSaveSummary(row.doc_id, row.skuSummary)}
                                                    size="small"
                                                    loading={editSummary[`load_${row.doc_id}`] || false}
                                                    variant="outlined"
                                                    aria-label={`Save - ${row.title}`}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        padding:0,
                                                        width: 35,
                                                        height: 35,
                                                        borderRadius: '3px'
                                                    }}
                                                >
                                                    <SaveIcon aria-hidden="true"  />
                                                </LoadingButton>
                                                <Button 
                                                    onClick={() => handleCancelSummary(row.doc_id)}
                                                    size="small"
                                                    color="error"
                                                    variant="outlined" 
                                                    aria-label={`Cancel - ${row.title}`}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        padding: 0,
                                                        width: 35,
                                                        height: 35,
                                                        borderRadius: '3px'
                                                    }}
                                                >
                                                    <ClearIcon aria-hidden="true"  />
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{display:'flex', gap:'15px'}}>
                                            <Box sx={{flex: 1}}>{editSummary[`text_${row.doc_id}`] || row.skuSummary?.summary}</Box>
                                            <Box sx={{textAlign: 'right'}}>
                                                <Button 
                                                    onClick={() => handleEditSummary(row.doc_id, row.skuSummary?.summary)}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined" 
                                                    aria-label={`Edit - ${row.title}`}
                                                    sx={{borderRadius: '3px', width: 35, height: 35, minWidth:'auto',
                                                        // ".MuiSvgIcon-fontSizeMedium": {
                                                        //     width:'.85em',
                                                        //     height:'.85em'
                                                        // }
                                                    }}
                                                >
                                                    <EditIcon sx={{fontSize:'20px'}} aria-hidden="true" />
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <ScoreInsights relevancy={row.skuSummary?.relevance_score} sentiments={row.skuSummary?.sentiment_score} />
                                </TableCell>
                                <TableCell className={style.bsciTd}>
                                    {/* {bsciCalc(bsciScore)[0] !== 'NA' && <span className={`${style.bsciScore} ${style[bsciCalc(bsciScore)[0]]}`}></span>} {bsciCalc(bsciScore)[1]} */}
                                    <BmpBsci bsci={bsciScore} />
                                </TableCell>
                                
                                <TableCell sx={{padding: '5px'}}>
                                    <BMPReviewThemeTable 
                                        themesData={themes} 
                                        docId={row.doc_id} 
                                        mainTableData={row} 
                                        statusColor={statusColor} 
                                        isRejected={row.skuSummary?.content_status === 'rejected'} 
                                        locale={locale} 
                                        setRefreshData={setRefreshData} 
                                        pendingThemeIds={pendingThemeIds}
                                        setPendingThemeIds={setPendingThemeIds}
                                        setSnackbarState={setSnackbarState}
                                    />
                                </TableCell>
                                <TableCell>
                                    <BmpStatus status={row.skuSummary?.content_status} />
                                </TableCell>
                                <TableCell>
                                    {dateFormatter(row.skuSummary?.updatedDate)}
                                </TableCell>
                                
                            </TableRow>
                        );
                    })}
                    {/* {emptyRows > 0 && (
                        <TableRow
                        style={{
                            height: 33 * emptyRows,
                        }}
                        >
                        <TableCell colSpan={6} />
                        </TableRow>
                    )} */}
                </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={loadedDataList?.totalCount || 10}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                    ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                        marginBottom: '15px'
                    }
                }}
            />

            </Paper>
        </Box>
    )
}

